/** Pure room-level copy: lobby status, start readiness, pause wording and the
 * announcements a client derives by diffing two RoomViews. Everything here reads only
 * public RoomView fields (names, seats, readiness, host, game id, settings, status), so
 * no announcement can contain a role, word, hand or ballot. */
import type { RoomView } from "../../server/protocol";

type Player = RoomView["players"][number];
const plural = (n: number, one = "player", many = "players") =>
  `${n} ${n === 1 ? one : many}`;
/** "Zoe", "Zoe and Sam", "3 players". */
export function nameList(names: string[]): string {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return plural(names.length);
}

export type LobbyStatus = {
  text: string;
  /** Active seats that are offline, named first in the status line. */
  offline: Player[];
  /** Host may start: count in range, every seat connected, every non-host ready. */
  startable: boolean;
};
export function lobbyStatus(input: {
  players: Player[];
  hostId: string;
  viewerId: string | null;
  isSpectator: boolean;
  min: number;
  max: number;
  gameName: string;
}): LobbyStatus {
  const { players, hostId, viewerId, min, max, gameName } = input;
  const host = players.find((p) => p.id === hostId);
  const hostName = host?.name ?? "the host";
  const isHost = viewerId !== null && viewerId === hostId && !input.isSpectator;
  const me = input.isSpectator ? undefined : players.find((p) => p.id === viewerId);
  const offline = players.filter((p) => !p.connected);
  const waiting = players.filter((p) => p.id !== hostId && !p.ready);
  const inRange = players.length >= min && players.length <= max;
  const startable = inRange && !offline.length && !waiting.length;
  let text: string;
  if (players.length > max) text = `${gameName} seats up to ${max}`;
  else if (offline.length)
    text = `${nameList(offline.map((p) => (p.id === viewerId ? "You" : p.name)))} ${offline.length === 1 && offline[0].id !== viewerId ? "is" : "are"} offline`;
  else if (players.length < min)
    text = `${plural(min - players.length, "more player", "more players")} needed`;
  else if (isHost)
    text = waiting.length
      ? `Waiting for ${nameList(waiting.map((p) => p.name))} to get ready`
      : "Everyone’s in · start when you like";
  else if (me && !me.ready) text = "Tap Ready when you’re set";
  else if (me) {
    const others = waiting.filter((p) => p.id !== me.id);
    text = others.length
      ? `You’re ready · waiting for ${nameList(others.map((p) => p.name))}`
      : `You’re ready · waiting for ${hostName} to start`;
  } else
    text = waiting.length
      ? `Waiting for ${nameList(waiting.map((p) => p.name))} to get ready`
      : `Waiting for ${hostName} to start`;
  return { text, offline, startable };
}

/** Pause banner copy. The server sends neutral reasons; name the host here. */
export function pauseCopy(
  reason: string | null,
  hostName: string,
  viewerIsHost: boolean,
): { title: string; detail: string | null; timeUp: boolean } {
  if (reason === "More time?")
    return viewerIsHost
      ? { title: "Time’s up", detail: "Add 30 seconds to keep going.", timeUp: true }
      : { title: "Time’s up", detail: `Waiting for ${hostName} to add 30s.`, timeUp: true };
  if (reason === "Paused by the host.")
    return { title: viewerIsHost ? "Paused by you" : `Paused by ${hostName}`, detail: null, timeUp: false };
  return { title: "Game paused", detail: reason, timeUp: false };
}

export type Announcement = {
  kind: "host" | "you-host" | "game" | "settings" | "join" | "leave" | "promote" | "abandon";
  text: string;
};
/** Announcements for one RoomView transition. Never during an active match except host
 * transfer; abandon is announced on arrival back in the lobby. The viewer's own
 * actions are not announced back to them. */
export function announcements(
  prev: RoomView | null,
  next: RoomView,
  gameName: (id: string) => string,
  options: { removedByMe?: ReadonlySet<string> } = {},
): Announcement[] {
  if (!prev || prev.roomInstanceId !== next.roomInstanceId) return [];
  const out: Announcement[] = [];
  const name = (id: string) =>
    next.players.find((p) => p.id === id)?.name ??
    prev.players.find((p) => p.id === id)?.name ??
    "Someone";
  const me = next.playerId;
  const viewerIsHost = me !== null && next.hostId === me && !next.isSpectator;
  const host = name(next.hostId);
  if (prev.hostId !== next.hostId)
    out.push(
      me !== null && next.hostId === me
        ? { kind: "you-host", text: "You’re the host now" }
        : { kind: "host", text: `${host} is now the host` },
    );
  if (prev.status === "playing" && next.status === "lobby" && !viewerIsHost)
    out.push({ kind: "abandon", text: `${host} ended the game` });
  if (next.status === "playing") return out;
  if (!viewerIsHost) {
    if (prev.gameId !== next.gameId)
      out.push({ kind: "game", text: `${host} switched to ${gameName(next.gameId)}` });
    else if (JSON.stringify(prev.settings) !== JSON.stringify(next.settings))
      out.push({ kind: "settings", text: `${host} changed the settings` });
  }
  const before = new Map(prev.players.map((p) => [p.id, p]));
  const after = new Map(next.players.map((p) => [p.id, p]));
  const joined = next.players.filter((p) => !before.has(p.id) && p.id !== me);
  const left = prev.players.filter(
    (p) => !after.has(p.id) && p.id !== me && !options.removedByMe?.has(p.id),
  );
  const promoted = next.players.filter(
    (p) => p.id !== me && !p.isSpectator && before.get(p.id)?.isSpectator,
  );
  const players = joined.filter((p) => !p.isSpectator);
  const watchers = joined.filter((p) => p.isSpectator);
  if (players.length)
    out.push({ kind: "join", text: `${nameList(players.map((p) => p.name))} joined` });
  if (watchers.length)
    out.push({
      kind: "join",
      text: `${nameList(watchers.map((p) => p.name))} ${watchers.length === 1 ? "is" : "are"} watching`,
    });
  if (promoted.length)
    out.push({ kind: "promote", text: `${nameList(promoted.map((p) => p.name))} joined the game` });
  if (left.length)
    out.push({ kind: "leave", text: `${nameList(left.map((p) => p.name))} left` });
  return out;
}

/** True when the server disambiguated the name this browser asked for ("Arnav" → "Arnav 2"). */
export function renamedOnJoin(requested: string, actual: string): boolean {
  const key = (v: string) => v.normalize("NFC").trim().toLocaleLowerCase("en");
  return key(requested) !== key(actual);
}
